import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Hook to fetch KYC dropdown lists from the ViaMobi backend API.
 * Calls a single endpoint: GET /mobi/kyc-lists
 * Authenticated by X-API-KEY (same as other /mobi/ endpoints).
 *
 * @param {{ baseUrl: string, token: string, enabled: boolean }} options
 * @returns {{ listPurposes: Array, listOrigins: Array, listOcupations: Array, listRelations: Array, loading: boolean, error: string|null }}
 */
export const useKYCLists = ({ baseUrl, token, enabled }) => {
  const [listPurposes, setListPurposes] = useState([]);
  const [listOrigins, setListOrigins] = useState([]);
  const [listOcupations, setListOcupations] = useState([]);
  const [listRelations, setListRelations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !baseUrl) return;

    const headers = token ? { 'X-API-KEY': token } : {};
    let cancelled = false;

    const fetchLists = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`${baseUrl}/mobi/kyc-lists`, { headers });

        if (cancelled) return;

        const data = response.data;
        if (data.purposes) setListPurposes(data.purposes);
        if (data.origins) setListOrigins(data.origins);
        if (data.industries) setListOcupations(data.industries);
        if (data.relationships) setListRelations(data.relationships);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load KYC lists');
          console.error('useKYCLists error:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchLists();

    return () => {
      cancelled = true;
    };
  }, [baseUrl, token, enabled]);

  return { listPurposes, listOrigins, listOcupations, listRelations, loading, error };
};

export default useKYCLists;
